import { ConfigProvider } from "antd";
import { useAppContext } from "utils/context";
import { Routes, Route, useLocation } from "react-router-dom";
import { Suspense, useEffect, useMemo } from "react";
import { setRouterPath, setTitle } from "store/router/action";
import { useDispatch, useSelector } from "react-redux";
import { handlePageTitle } from "layouts/services/data.transformers";
import { useTranslation } from "react-i18next";
import { DefaultLayout, ChatLayout, PageLayout } from "layouts";
import { defaultRoutes, chatRoutes, pageRoutes } from "utils/routes";
import {
  WebengagePageEvent,
  handlePageEventName,
  handlePageMetrixEvents,
  handleWebengageObjectId,
  pageTitle,
} from "containers/services/events.data";
import BankActivation from "containers/bankActivation";
import TransactionsReceipt from "containers/transactionsReceipt";
import OperationsReceipt from "containers/operationsReceipt";
import enUS from "antd/locale/en_US";
import faIR from "antd/locale/fa_IR";
import Login from "pages/login";
import Callback from "pages/callBack";
import Notfound from "pages/notFound";
import Receipt from "pages/receipt";
import Rotaion from "pages/rotation";
import CardOnboarding from "containers/cardOnboarding";
import PMAOnboarding from "containers/pmaOnboarding";
import PMAReceipt from "containers/pmaReceipt";
import PMAInventoryIncrease from "containers/pmaInventoryIncrease";
import CardPromotion from "containers/cardPromotion";
import VoucherReceipt from "containers/voucherReceipt";
import useFirebase from "components/notification";
import Cookies from "js-cookie";

function App() {
  useFirebase();
  const { language, screen, metrixInstance } = useAppContext();
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch();
  const queries = new URLSearchParams(location?.search);
  const webengageEvents =
    Cookies.get("webengageEvents") &&
    JSON.parse(Cookies.get("webengageEvents"));
  const prevMenu = useSelector((state) => state?.routerReducer?.prevMenu);
  const pma = useSelector((state) => state?.pmaReducer?.pma);
  const userPma = useSelector((state) => state?.pmaReducer?.userPma);
  const fundName = useSelector(
    (state) => state?.fundReducer?.userFund?.fund_name
  );
  const dscode = useSelector((state) => state?.fundReducer?.userFund?.ds_code);
  const faqId = useSelector((state) => state?.faqReducer?.id);
  const rulesTitle = useSelector((state) => state?.generalReducer?.pdfTitle);

  const pageId = location.pathname?.includes("pma") ? pma?.id : dscode;
  const webengageCondition = webengageEvents?.page_visit?.includes(
    pageTitle(location.pathname, pageId)
  );

  const locale = {
    ...faIR,
    DatePicker: {
      ...faIR.DatePicker,
      lang: {
        ...faIR.DatePicker.lang,
        monthFormat: "MMMM",
      },
    },
    Calendar: {
      ...faIR.Calendar,
      lang: {
        ...faIR.Calendar.lang,
        monthFormat: "MMMM",
      },
    },
  };

  const getLocale = () => (language === "en" ? enUS : locale);
  const getDirection = () => (language === "en" ? "ltr" : "rtl");

  useEffect(() => {
    dispatch(setRouterPath(location?.pathname));

    if (webengageCondition) {
      const timeoutId = setTimeout(() => {
        WebengagePageEvent(
          handlePageEventName(pageTitle(location.pathname, pageId)),
          pageTitle(location.pathname, pageId),
          pageTitle(prevMenu, pageId),
          handleWebengageObjectId(location.pathname, pageId)
        );
      }, 0);

      return () => clearTimeout(timeoutId);
    }
  }, [dispatch, location.pathname, webengageCondition]);

  useEffect(() => {
    dispatch(
      setTitle(
        handlePageTitle(
          location?.pathname,
          userPma,
          pma,
          dscode,
          fundName,
          faqId,
          rulesTitle,
          t
        )
      )
    );
    // eslint-disable-next-line
  }, [
    location?.pathname,
    dispatch,
    t,
    userPma,
    dscode,
    fundName,
    rulesTitle,
    pma,
  ]);

  useEffect(() => {
    const isTwa = queries.get("isTwa");
    if (isTwa && isTwa === "true") localStorage.setItem("isTwa", "true");
  }, []);

  useMemo(async () => {
    await handlePageMetrixEvents(
      metrixInstance,
      location.pathname,
      dscode,
      pma?.id
    );
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const loading = (
    <div className="animated fadeIn pt-1 text-center">Loading...</div>
  );

  return (
    <ConfigProvider locale={getLocale()} direction={getDirection()}>
      <Suspense fallback={loading}>
        <Routes>
          {screen ? (
            <>
              <Route path="/" element={<Login />} />
              <Route path="/callback" element={<Callback />} />
              <Route path="/receipt/*" element={<Receipt />} />
              <Route path="/bank/activation" element={<BankActivation />} />
              <Route path="/bank/callback/block" element={<BankActivation />} />
              <Route
                path="/bank/callback/set-pin"
                element={<BankActivation />}
              />
              <Route path="/bank/onboarding" element={<CardOnboarding />} />
              <Route path="/pma/onboarding" element={<PMAOnboarding />} />
              <Route path="/pma/receipt" element={<PMAReceipt />} />

              <Route
                path="/pma/inventory-increase"
                element={<PMAInventoryIncrease />}
              />

              <Route path="/bank/promotion" element={<CardPromotion />} />
              <Route
                path="/bank/transactions/receipt/*"
                element={<TransactionsReceipt />}
              />
              <Route
                path="/bank/operations/receipt/:id"
                element={<OperationsReceipt />}
              />
              <Route path="/voucher/receipt" element={<VoucherReceipt />} />
              <Route path="*" element={<Notfound />} />
              <Route path="/" element={<DefaultLayout />}>
                {defaultRoutes.map((route) => {
                  return (
                    <Route
                      path={route.path}
                      key={route.path}
                      element={route.element}
                    />
                  );
                })}
              </Route>
              <Route path="/" element={<PageLayout />}>
                {pageRoutes.map((route) => {
                  return (
                    <Route
                      path={route.path}
                      key={route.path}
                      element={route.element}
                    />
                  );
                })}
              </Route>
              <Route path="/" element={<ChatLayout />}>
                {chatRoutes.map((route) => {
                  return (
                    <Route
                      path={route.path}
                      key={route.path}
                      element={route.element}
                    />
                  );
                })}
              </Route>
            </>
          ) : (
            <Route path="*" element={<Rotaion />} />
          )}
        </Routes>
      </Suspense>
    </ConfigProvider>
  );
}

export default App;
